import React from 'react';

import { useTranslation } from '@lt/localization';
import { Button, Throbber } from '@lt/components';

import ReviewCard from 'src/components/Reviews/ReviewCard/ReviewCard';

import type { ReviewsProps } from '@lt/api/dist/methods/hotelReviews/types';
import ErrorBanner from 'src/components/Reviews/components/ErrorBanner';

import { ReviewCardMobileSkeleton } from '@lt/components/icons-react/skeletons';
import styles from './reviewsList.module.css';

export interface ReviewListProps {
  items: ReviewsProps[];
  showMore: () => void;
  isShowMoreReviews: boolean;
  isLoadingNew: boolean;
  isFetching: boolean;
  isErrorResult: boolean;
  onClickRetry: () => void;
}

export const ReviewList = ({
  items,
  isShowMoreReviews,
  showMore,
  isLoadingNew,
  isFetching,
  isErrorResult,
  onClickRetry,
}: ReviewListProps): JSX.Element | null => {
  const { t } = useTranslation();
  if (isErrorResult) return <ErrorBanner onClickRetry={onClickRetry} />;
  if (isFetching)
    return (
      <div>
        <ReviewCardMobileSkeleton className={styles.skeleton} />
        <ReviewCardMobileSkeleton className={styles.skeleton} />
        <ReviewCardMobileSkeleton className={styles.skeleton} />
      </div>
    );

  const handleClick = () => {
    showMore();
  };

  return (
    <>
      <div>
        {items.map((item) => (
          <ReviewCard
            className={styles.reviewCard}
            key={item.id}
            id={item.id}
            content={item.content}
            name={item.name}
            createdAt={item.createdAt}
            averageRating={item.averageRating}
            avatarUrl={item.avatarUrl}
            answer={item.answer}
            answeredAt={item.answeredAt}
            reviewPlus={item.reviewPlus}
            reviewMinus={item.reviewMinus}
            title={item.title}
            images={item.images}
            isMobile
            phrase={item.phrase}
            tags={item.tags}
          />
        ))}
        {isShowMoreReviews && (
          <div className={styles.showMoreContainer}>
            <Button
              className={styles.buttonShowMore}
              onClick={handleClick}
              size="flex"
            >
              {isLoadingNew ? (
                <Throbber size={20} color="rgb(0, 122, 255)" />
              ) : (
                t('hotel.reviews.moreReviews')
              )}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
